import React, { useCallback, useRef, useState } from 'react'
import FormV3 from './form'
import { useProcessDocument } from './hooks/useProcessDocument'
import { useVirtualization } from './hooks/useVirtualization'
import useZoom from './hooks/zoom'
import ImageViewer from './ImageViewer'
import DynamicFormAndPDFViewerLoading from './loading'
import { PDFViewer } from './pdfViewer'

const ZOOM_FACTOR = 0.2
const MIN_SCALE = 0.2

const ZoomArea = ({ zoomOut, zoomIn }) => {
  return (
    <div
      style={{ backgroundImage: 'linear-gradient(180deg, #828589, #0e162400)', height: 45 }}
      className="dome-d-flex dome-align-center dome-justify-center dome-gap9"
    >
      <div onClick={() => zoomOut(ZOOM_FACTOR)} style={{ width: 32, height: 32, pointerEvents: 'auto' }}>
        <div className="dome-full-height dome-d-flex dome-align-center dome-justify-center dome-cursor-pointer">
          <i style={{ color: 'white' }} className="fa-sharp fa-regular fa-magnifying-glass-minus"></i>
        </div>
      </div>
      <div onClick={() => zoomIn(ZOOM_FACTOR)} style={{ width: 32, height: 32, pointerEvents: 'auto' }}>
        <div className="dome-full-height dome-d-flex dome-align-center dome-justify-center dome-cursor-pointer">
          <i style={{ color: 'white' }} className="fa-regular fa-magnifying-glass-plus "></i>
        </div>
      </div>
    </div>
  )
}

function DynamicFormAndPDFViewer({ document }) {
  const ref = useRef()
  const [scale, setScale] = useState(1)

  const timerRef = useRef()
  const zoomAreaElRef = useRef()
  const [displayZoomArea, setDisplayZoomArea] = useState(false)

  const { pageLayouts, isLoading } = useProcessDocument({ document, containerRef: ref })
  const { pagesMetadata, visiblePages, maxWidth, maxHeight } = useVirtualization({
    pageLayouts,
    scrollRef: ref,
    scaleFactor: scale,
  })

  const zoomIn = useCallback(
    (zoomFactor) => {
      const newScale = scale + (zoomFactor ?? ZOOM_FACTOR)
      setScale(Number(newScale.toFixed(1)))
    },
    [scale]
  )

  const zoomOut = useCallback(
    (zoomFactor) => {
      const newScale = scale - (zoomFactor ?? ZOOM_FACTOR)
      setScale(Math.max(MIN_SCALE, Number(newScale.toFixed(1))))
    },
    [scale]
  )

  useZoom(zoomIn, zoomOut)

  const handleMouseMove = useCallback((e) => {
    setDisplayZoomArea(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    if (zoomAreaElRef.current?.contains(e.target)) return
    timerRef.current = setTimeout(() => {
      setDisplayZoomArea(false)
    }, 2000)
  }, [])

  return (
    <div
      onMouseMove={handleMouseMove}
      style={{ background: '#DBDBDB' }}
      className="dome-full-width dome-full-height dome-relative"
    >
      {isLoading && <DynamicFormAndPDFViewerLoading />}
      {!isLoading && displayZoomArea && (
        <div ref={zoomAreaElRef} style={{ position: 'absolute', width: '100%', zIndex: 2, pointerEvents: 'none' }}>
          <ZoomArea zoomIn={zoomIn} zoomOut={zoomOut} />
        </div>
      )}
      <div ref={ref} className="dome-full-width dome-full-height" style={{ overflow: 'auto', position: 'relative' }}>
        {!isLoading && (
          <div style={{ width: 'fit-content' }} className="dome-relative dome-margin-auto">
            {pagesMetadata.map((page) => {
              if (page.type === 'form')
                return <FormV3 formId={page.formId} width={page.width} height={page.height} top={page.top} />
              if (page.type === 'image')
                return <ImageViewer url={page.url} width={page.width} height={page.height} top={page.top} />
            })}
            {visiblePages.map((page) => {
              if (page.type === 'pdf')
                return (
                  <PDFViewer
                    key={`${page.id},${page.index}`}
                    pageInstance={page.pageProxy}
                    pageIndex={page.index}
                    top={page.top}
                    width={page.width}
                    height={page.height}
                    scaleFactor={page.scale}
                  />
                )
            })}
            <div style={{ height: maxHeight, width: maxWidth, pointerEvents: 'none' }}></div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DynamicFormAndPDFViewer
